import { DATAFEED_CONFIG, QUANT_NG_API_SESSION_RENAMES, RESOLUTION_MAP } from './constants.js';
import type { TradingViewDatafeedService } from './index.js';
import { buildSessionHolidays, buildSessionStringFromTypicalSchedule, mapScheduleEntryToSessionId } from './util.js';
import type { QuantClient } from '@thinkalpha/quant-ws-client/client.js';
import type {
    Bar,
    DatafeedErrorCallback,
    HistoryCallback,
    LibrarySessionId,
    LibrarySymbolInfo,
    OnReadyCallback,
    PeriodParams,
    ResolveCallback,
    ResolutionString,
    SearchSymbolResultItem,
    SubscribeBarsCallback,
    Timezone,
    SymbolResolveExtension,
    LibrarySubsessionInfo,
    IDatafeedChartApi,
} from 'charting_library';
import { inject, injectable } from 'src/features/ioc/index.js';
import { quantWsClient } from 'src/lib/newQuant/index.js';
import type { LightweightChartBarDataService } from 'src/services/LightweightChartBarDataService/index.js';
import type { SecurityMasterService } from 'src/services/SecurityMasterService/index.js';
import type { ReactBindings } from 'src/types/bindings.js';

@injectable()
export class TradingViewDatafeedServiceImpl implements TradingViewDatafeedService, IDatafeedChartApi, Disposable {
    readonly #log: ReturnType<ReactBindings['Logger']['getSubLogger']>;
    readonly #securityMasterService: SecurityMasterService;
    readonly #lastBarsCache = new Map<string, Bar>();
    readonly #quantClient: QuantClient;
    readonly #barDataService: LightweightChartBarDataService;

    constructor(
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('SecurityMasterService') securityMasterService: ReactBindings['SecurityMasterService'],
        @inject('LightweightChartBarDataService') barDataService: LightweightChartBarDataService,
    ) {
        this.#log = logger.getSubLogger({ name: 'TradingViewDatafeedService' });
        this.#quantClient = quantWsClient; // TODO: inject this?
        this.#securityMasterService = securityMasterService;
        this.#barDataService = barDataService;

        if (TRACE) {
            this.#log.trace('TradingViewDatafeedService initialized');
        }
    }

    onReady(callback: OnReadyCallback): void {
        if (TRACE) {
            this.#log.trace('onReady called');
        }
        setTimeout(() => callback(DATAFEED_CONFIG));
    }

    async searchSymbols(
        userInput: string,
        exchange: string,
        symbolType: string,
        onResultReadyCallback: (items: SearchSymbolResultItem[]) => void,
    ): Promise<void> {
        if (TRACE) {
            this.#log.trace('Searching symbols', { userInput, exchange, symbolType });
        }

        try {
            const matchingEntries =
                await this.#securityMasterService.fetchSecurityMasterEntriesMatchingPrefix(userInput);

            const results: SearchSymbolResultItem[] = matchingEntries.slice(0, 50).map((entry) => ({
                symbol: entry.todayTicker,
                full_name: entry.name,
                description: entry.description,
                exchange: entry.exchange,
                type: 'stock',
                ticker: entry.todayTicker,
            }));

            onResultReadyCallback(results);
        } catch (error) {
            this.#log.error('Symbol search failed', { error });
            onResultReadyCallback([]);
        }
    }

    async resolveSymbol(
        symbolName: string,
        onSymbolResolvedCallback: ResolveCallback,
        onResolveErrorCallback: DatafeedErrorCallback,
        extension?: SymbolResolveExtension,
    ): Promise<void> {
        const log = this.#log.getSubLogger({ method: 'resolveSymbol' });

        if (TRACE) {
            log.trace('Resolving symbol', { symbolName });
        }

        try {
            const entry = await this.#securityMasterService.fetchSecurityMasterEntry(symbolName);

            // Fetch market data from market master
            const marketData = await this.#quantClient.getMarketInfo(entry.PrimaryMIC);
            if (!marketData) {
                log.error('Market data not found in market master', {
                    mic: entry.PrimaryMIC,
                    symbol: symbolName,
                });
                onResolveErrorCallback('Market data unavailable');
                return;
            }

            // Use timezone from market master
            let timezone: Timezone = marketData.timezone as Timezone;
            if (timezone.toString() === 'US/Eastern') {
                // TradingView expects "America/New_York" instead of "US/Eastern"
                timezone = 'America/New_York' as Timezone;
            }

            const subsessionMap = new Map<string, LibrarySubsessionInfo>();

            if (TRACE) {
                log.trace('Processing schedule entries', {
                    symbol: symbolName,
                    count: marketData.typicalSchedule.length,
                });
                marketData.typicalSchedule.forEach((entry, index) => {
                    // eslint-disable-next-line @thinkalpha/eslint-rules/wrap-log-trace -- it IS wrapped with a trace check
                    log.trace(`Schedule entry ${index}`, {
                        name: entry.name,
                        phase: entry.phase,
                        status: entry.status,
                        startTime: entry.startTime.toString(),
                        endTime: entry.endTime.toString(),
                        mappedSessionId: mapScheduleEntryToSessionId(entry),
                    });
                });
            }

            // Filter out closed entries and remove overlapping schedules globally
            // If a schedule is fully enclosed by another, skip it
            // If a schedule fully encloses existing ones, replace them
            const filteredScheduleEntries: typeof marketData.typicalSchedule = [];

            for (const scheduleEntry of marketData.typicalSchedule) {
                if (scheduleEntry.status === 'Closed') continue;

                const currentStart = scheduleEntry.startTime;
                const currentEnd = scheduleEntry.endTime;

                // Check if the current schedule is fully enclosed by an existing one
                const isFullyEnclosed = filteredScheduleEntries.some(
                    (existing) =>
                        existing.startTime.compareTo(currentStart) <= 0 && existing.endTime.compareTo(currentEnd) >= 0,
                );

                if (isFullyEnclosed) {
                    if (TRACE) {
                        log.trace('Skipping schedule entry fully enclosed by existing', {
                            currentStart: currentStart.toString(),
                            currentEnd: currentEnd.toString(),
                            scheduleEntry: {
                                name: scheduleEntry.name,
                                phase: scheduleEntry.phase,
                                status: scheduleEntry.status,
                            },
                        });
                    }
                    continue;
                }

                // Remove any existing entries that are fully enclosed by the current one
                for (let i = filteredScheduleEntries.length - 1; i >= 0; i--) {
                    const existing = filteredScheduleEntries[i];
                    const isEnclosed =
                        currentStart.compareTo(existing.startTime) <= 0 && currentEnd.compareTo(existing.endTime) >= 0;
                    if (isEnclosed) {
                        if (TRACE) {
                            log.trace('Removing existing schedule entry enclosed by new one', {
                                removedStart: existing.startTime.toString(),
                                removedEnd: existing.endTime.toString(),
                                newStart: currentStart.toString(),
                                newEnd: currentEnd.toString(),
                            });
                        }
                        filteredScheduleEntries.splice(i, 1);
                    }
                }

                filteredScheduleEntries.push(scheduleEntry);
            }

            const fullMarketSessionStringOriginal = buildSessionStringFromTypicalSchedule(filteredScheduleEntries);
            // const fullMarketSessionString = alignSessionToBarBoundary(fullMarketSessionStringOriginal, 60);
            const fullMarketSessionString = fullMarketSessionStringOriginal;

            // Group filtered entries by session ID
            const scheduleEntriesBySessionId = new Map<string, typeof marketData.typicalSchedule>();
            for (const scheduleEntry of filteredScheduleEntries) {
                const tvSessionId = mapScheduleEntryToSessionId(scheduleEntry);
                if (!scheduleEntriesBySessionId.has(tvSessionId)) {
                    scheduleEntriesBySessionId.set(tvSessionId, []);
                }
                scheduleEntriesBySessionId.get(tvSessionId)!.push(scheduleEntry);
            }

            // Build subsession map from grouped entries
            // Store both original and aligned sessions
            const originalSessionBySubsessionId = new Map<string, string>();
            const alignedSessionBySubsessionId = new Map<string, string>();
            for (const [tvSessionId, entries] of scheduleEntriesBySessionId) {
                const firstEntry = entries[0];
                if (!firstEntry) continue;

                const sessionOriginal = buildSessionStringFromTypicalSchedule(entries);
                // const sessionAligned = alignSessionToBarBoundary(sessionOriginal, 60);
                const sessionAligned = sessionOriginal;
                originalSessionBySubsessionId.set(tvSessionId, sessionOriginal);
                alignedSessionBySubsessionId.set(tvSessionId, sessionAligned);

                // Subsessions use aligned session for bar alignment
                // session_display shows actual market hours
                subsessionMap.set(tvSessionId, {
                    id: tvSessionId as LibrarySessionId,
                    description:
                        tvSessionId === 'regular'
                            ? 'Regular Trading Hours'
                            : (QUANT_NG_API_SESSION_RENAMES[tvSessionId] ?? firstEntry.name ?? firstEntry.phase),
                    session: sessionAligned,
                    'session-display': sessionOriginal,
                });

                if (TRACE) {
                    log.trace('Built subsession from entries', {
                        sessionId: tvSessionId,
                        entryCount: entries.length,
                        session: sessionAligned,
                        sessionDisplay: sessionOriginal,
                    });
                }
            }

            const subsessions: LibrarySubsessionInfo[] = Array.from(subsessionMap.values());

            if (subsessions.length > 1) {
                subsessions.push({
                    id: 'extended',
                    description: QUANT_NG_API_SESSION_RENAMES['extended'] ?? 'Extended Trading Hours',
                    session: fullMarketSessionString,
                    'session-display': fullMarketSessionStringOriginal,
                });
                originalSessionBySubsessionId.set('extended', fullMarketSessionStringOriginal);
                alignedSessionBySubsessionId.set('extended', fullMarketSessionString);
            }

            if (TRACE) {
                log.trace('Constructed subsessions', { symbolName, subsessions });
            }

            // Fetch holiday information
            const sessionHolidays = buildSessionHolidays(marketData);

            const symbolInfo: LibrarySymbolInfo = {
                ticker: symbolName,
                name: symbolName,
                description: symbolName,
                type: 'stock', // entry.securityType,
                exchange: entry.exchange,
                listed_exchange: entry.exchange,
                timezone,
                minmov: 1,
                pricescale: 100,
                has_intraday: true,
                intraday_multipliers: ['1', '5', '10', '30'],
                has_daily: true,
                daily_multipliers: ['1'],
                has_seconds: true,
                seconds_multipliers: ['10', '30'],
                has_empty_bars: true,
                has_weekly_and_monthly: true,
                weekly_multipliers: ['1'],
                monthly_multipliers: ['1', '12'],
                supported_resolutions: DATAFEED_CONFIG.supported_resolutions,
                visible_plots_set: 'ohlcv',
                volume_precision: 0,
                data_status: 'streaming',
                format: 'price',

                // session uses aligned times for bar alignment (9:00, 10:00, etc.)
                session: alignedSessionBySubsessionId.get(extension?.session ?? 'regular') ?? fullMarketSessionString,
                // session_display shows actual market hours in UI (9:30, etc.)
                session_display:
                    originalSessionBySubsessionId.get(extension?.session ?? 'regular') ??
                    fullMarketSessionStringOriginal,
                subsession_id: extension?.session ?? 'regular',
                subsessions,
                session_holidays: sessionHolidays,
            };

            if (TRACE) {
                log.trace('Symbol resolved', { symbolName, subsessions, symbolInfo });
            }

            onSymbolResolvedCallback(symbolInfo);
        } catch (error) {
            log.error('Failed to resolve symbol', { symbolName, error });
            onResolveErrorCallback('unknown_symbol');
        }
    }

    async getBars(
        symbolInfo: LibrarySymbolInfo,
        resolution: ResolutionString,
        periodParams: PeriodParams,
        onHistoryCallback: HistoryCallback,
        onErrorCallback: DatafeedErrorCallback,
    ): Promise<void> {
        const { from, to } = periodParams;

        if (TRACE) {
            this.#log.trace('Getting bars', { symbol: symbolInfo.ticker, resolution, from, to });
        }

        const barLength = RESOLUTION_MAP[resolution];
        if (!barLength) {
            onErrorCallback(`Invalid resolution: ${resolution}`);
            return;
        }

        if (!symbolInfo.ticker) {
            onErrorCallback('No symbol ticker');
            return;
        }

        return await this.getBarsUsingBardex(
            symbolInfo,
            barLength.bardex,
            periodParams,
            onHistoryCallback,
            onErrorCallback,
        );
    }

    async getBarsUsingBardex(
        symbolInfo: LibrarySymbolInfo,
        barLength: string,
        periodParams: PeriodParams,
        onHistoryCallback: HistoryCallback,
        onErrorCallback: DatafeedErrorCallback,
    ): Promise<void> {
        const ticker = symbolInfo.ticker;
        if (!ticker) {
            onErrorCallback('No symbol ticker');
            return;
        }

        const { from, to, firstDataRequest, countBack } = periodParams;

        try {
            // Bardex needs the display session string (e.g. "0930-1600"), not
            // the hour-aligned `session` field which crashes Bardex.
            const sessionWindow = symbolInfo.session_display ?? symbolInfo.session;

            const ohlcvBars = await this.#barDataService.fetchHistory({
                ticker,
                barLengthBardex: barLength,
                from,
                to,
                countBack,
                sessionId: symbolInfo.subsession_id ?? 'regular',
                sessionWindow,
                timezone: symbolInfo.timezone,
            });

            const bars: Bar[] = ohlcvBars.map(
                (b) =>
                    ({
                        time: b.time,
                        open: b.open,
                        high: b.high,
                        low: b.low,
                        close: b.close,
                        volume: b.volume,
                    }) satisfies Bar as Bar,
            );

            if (firstDataRequest && bars.length > 0) {
                this.#lastBarsCache.set(ticker, { ...bars[bars.length - 1] });
                if (TRACE) {
                    this.#log.trace('Cached last bar', { symbol: ticker });
                }
            }

            onHistoryCallback(bars, { noData: bars.length === 0 });
        } catch (error) {
            this.#log.error('Failed to get bars from Bardex', { error });
            onErrorCallback(error instanceof Error ? error.message : 'Unknown error');
        }
    }

    readonly #subscriberUIDToTableModelMap = new Map<string, Disposable>();

    subscribeBars(
        symbolInfo: LibrarySymbolInfo,
        resolution: ResolutionString,
        onRealtimeCallback: SubscribeBarsCallback,
        subscriberUID: string,
        // onResetCacheNeededCallback: () => void,
    ): void {
        if (TRACE) {
            this.#log.trace('Subscribing to bars', { subscriberUID, symbol: symbolInfo.ticker });
        }

        if (this.#subscriberUIDToTableModelMap.has(subscriberUID)) {
            this.#log.warn('Subscription already exists for subscriberUID', { subscriberUID });
            return;
        }

        const barLength = RESOLUTION_MAP[resolution];
        if (!barLength || !symbolInfo.ticker) {
            this.#log.error('Invalid subscription parameters');
            return;
        }

        // For midnight-spanning detection we need the full market (extended)
        // session — server timestamps mark the end of the extended trading
        // day regardless of which session the user selected.
        const fullMarketSession =
            symbolInfo.subsessions?.find((s) => s.id === 'extended')?.session ?? symbolInfo.session;

        const disposable = this.#barDataService.subscribeRealtime(
            {
                ticker: symbolInfo.ticker,
                barLength: barLength.platform,
                // Any non-regular subsession (premarket/postmarket/extended) must
                // subscribe to the extended stream — the regular stream omits
                // those bars entirely. `mapScheduleEntryToSessionId` returns
                // 'premarket' / 'regular' / 'postmarket' and the synthetic
                // combined session is 'extended', so anything other than
                // 'regular' belongs on the extended stream. `?? 'regular'`
                // keeps the default (unset subsession_id) on the regular
                // stream, matching the rest of this file's convention.
                extended: (symbolInfo.subsession_id ?? 'regular') !== 'regular',
                fullMarketSession,
                timezone: symbolInfo.timezone,
            },
            (bar) => {
                // The bar service intentionally reuses the same mutable bar
                // object across ticks (documented in its impl). Forward a
                // fresh snapshot so charting_library consumers can't observe
                // a prior tick's fields after the bar service mutates it.
                onRealtimeCallback({
                    time: bar.time,
                    open: bar.open,
                    high: bar.high,
                    low: bar.low,
                    close: bar.close,
                    volume: bar.volume,
                } satisfies Bar as Bar);
            },
        );

        this.#subscriberUIDToTableModelMap.set(subscriberUID, disposable);
    }

    unsubscribeBars(subscriberUID: string): void {
        if (TRACE) {
            this.#log.trace('Unsubscribing from bars', { subscriberUID });
        }

        const item = this.#subscriberUIDToTableModelMap.get(subscriberUID);

        if (!item) {
            if (TRACE) {
                this.#log.info('When unsubscribing, no subscription found for subscriberUID', { subscriberUID });
            }
            return;
        }

        if (TRACE) {
            this.#log.trace('Cleaning up subscription', {
                subscriberUID,
            });
        }

        item[Symbol.dispose]();
        this.#subscriberUIDToTableModelMap.delete(subscriberUID);
    }

    [Symbol.dispose](): void {
        if (TRACE) {
            this.#log.trace('TradingViewDatafeedService disposing');
        }

        for (const [, item] of this.#subscriberUIDToTableModelMap) {
            item[Symbol.dispose]();
        }
        this.#subscriberUIDToTableModelMap.clear();
        this.#lastBarsCache.clear();
    }
}
